import { getEnv, getNetworkPassphrase } from 'helpers/env';
import { createAsset } from 'helpers/token';

import { Environment } from 'types/env';
import { ClassicToken } from 'types/token';

import { ENV_PRODUCTION, ENV_TESTNET } from './env';

const AQUA_CODE = 'AQUA';
const AQUA_ISSUER = 'GBNZILSTVQZ4R7IKQDGHYGY2QXL5QOFJYQMXPKWRRM5PAV7Y4M67AQUA';

const USDC_CODE = 'USDC';
const USDC_ISSUER = 'GA5ZSEJYB37JRC5AVCIA5MOP4RHTM335X2KGX3IHOJAPP5RE34K4KZVN';

export const TESTNET_SHARED_ISSUER = 'GAHPYWLK6YRN7CVYZOO4H3VDRZ7PVF5UJGLZCSPAEIKJE2XSWF5LAGER';

const ICE_CODE = 'ICE';
const ICE_ISSUER_PRODUCTION = 'GAXSGZ2JM3LNWOO4WRGADISNMWO4HQLG4QBGUZRKH5ZHL3EQBGX73ICE';
// Testnet ICE distributor: issues ICE tokens for locking testnet AQUA.
const ICE_ISSUER_TESTNET = 'GAYYH44SS4OSFDY4WXMWM2BKRA2RG5M6NZULUQWGHKGFASFY2ZVI7IHX';

const GOV_ICE_CODE = 'governICE';
const UP_ICE_CODE = 'upvoteICE';
const D_ICE_CODE = 'dICE';
const GD_ICE_CODE = 'gdICE';
// Deprecated: downvoteICE was removed from the app. Kept only so users can
// clean up the leftover trustline of this asset.
const DOWN_ICE_CODE = 'downvoteICE';

type ClassicAssetConfig = { code: string; issuer: string };

export type EnvClassicAssetConfig = Record<Environment, ClassicAssetConfig>;

export type EnvClassicAssetName =
    | 'aqua'
    | 'usdc'
    | 'ice'
    | 'governIce'
    | 'upvoteIce'
    | 'dIce'
    | 'gdIce'
    | 'downvoteIce';

export type BaseClassicAssetData = {
    code: string;
    issuer: string;
    assetString: string;
    asset: ClassicToken;
    contract: string;
};

const createClassicAssetConfig = (code: string, issuer: string): ClassicAssetConfig => ({
    code,
    issuer,
});

const createEnvClassicAssetConfig = (
    production: ClassicAssetConfig,
    testnet: ClassicAssetConfig,
): EnvClassicAssetConfig => ({
    [ENV_PRODUCTION]: production,
    [ENV_TESTNET]: testnet,
});

const createTestnetAssetKey = ({
    code,
    issuer = TESTNET_SHARED_ISSUER,
}: {
    code: string;
    issuer?: string;
}) => `${code}:${issuer}`;

const createTestnetAssetInfo = ({
    code,
    image,
    home_domain,
    issuer = TESTNET_SHARED_ISSUER,
}: {
    code: string;
    image: string;
    home_domain: string;
    issuer?: string;
}) =>
    [
        createTestnetAssetKey({ code, issuer }),
        {
            code,
            issuer,
            image,
            home_domain,
        },
    ] as const;

const createTestnetDistribution = ({
    code,
    amount,
    issuer = TESTNET_SHARED_ISSUER,
}: {
    code: string;
    amount: string;
    issuer?: string;
}): [string, string] => [createTestnetAssetKey({ code, issuer }), amount];

const AQUA_CLASSIC_ASSET_CONFIG = createEnvClassicAssetConfig(
    createClassicAssetConfig(AQUA_CODE, AQUA_ISSUER),
    createClassicAssetConfig(AQUA_CODE, TESTNET_SHARED_ISSUER),
);

const USDC_CLASSIC_ASSET_CONFIG = createEnvClassicAssetConfig(
    createClassicAssetConfig(USDC_CODE, USDC_ISSUER),
    createClassicAssetConfig(USDC_CODE, TESTNET_SHARED_ISSUER),
);

const ICE_CLASSIC_ASSET_CONFIG = createEnvClassicAssetConfig(
    createClassicAssetConfig(ICE_CODE, ICE_ISSUER_PRODUCTION),
    createClassicAssetConfig(ICE_CODE, ICE_ISSUER_TESTNET),
);

const UP_ICE_CLASSIC_ASSET_CONFIG = createEnvClassicAssetConfig(
    createClassicAssetConfig(UP_ICE_CODE, ICE_ISSUER_PRODUCTION),
    createClassicAssetConfig(UP_ICE_CODE, ICE_ISSUER_TESTNET),
);

const D_ICE_CLASSIC_ASSET_CONFIG = createEnvClassicAssetConfig(
    createClassicAssetConfig(D_ICE_CODE, ICE_ISSUER_PRODUCTION),
    createClassicAssetConfig(D_ICE_CODE, ICE_ISSUER_TESTNET),
);

const GOVERN_ICE_CLASSIC_ASSET_CONFIG = createEnvClassicAssetConfig(
    createClassicAssetConfig(GOV_ICE_CODE, ICE_ISSUER_PRODUCTION),
    createClassicAssetConfig(GOV_ICE_CODE, ICE_ISSUER_TESTNET),
);

const GD_ICE_CLASSIC_ASSET_CONFIG = createEnvClassicAssetConfig(
    createClassicAssetConfig(GD_ICE_CODE, ICE_ISSUER_PRODUCTION),
    createClassicAssetConfig(GD_ICE_CODE, ICE_ISSUER_TESTNET),
);

// Deprecated: downvoteICE was removed from the app. Kept only so users can
// clean up the leftover trustline of this asset.
const DOWN_ICE_CLASSIC_ASSET_CONFIG = createEnvClassicAssetConfig(
    createClassicAssetConfig(DOWN_ICE_CODE, ICE_ISSUER_PRODUCTION),
    createClassicAssetConfig(DOWN_ICE_CODE, ICE_ISSUER_TESTNET),
);

export const ENV_CLASSIC_ASSETS_CONFIG: Record<EnvClassicAssetName, EnvClassicAssetConfig> = {
    aqua: AQUA_CLASSIC_ASSET_CONFIG,
    usdc: USDC_CLASSIC_ASSET_CONFIG,
    ice: ICE_CLASSIC_ASSET_CONFIG,
    governIce: GOVERN_ICE_CLASSIC_ASSET_CONFIG,
    upvoteIce: UP_ICE_CLASSIC_ASSET_CONFIG,
    dIce: D_ICE_CLASSIC_ASSET_CONFIG,
    gdIce: GD_ICE_CLASSIC_ASSET_CONFIG,
    downvoteIce: DOWN_ICE_CLASSIC_ASSET_CONFIG,
};

export const getEnvClassicAssetData = (name: EnvClassicAssetName): BaseClassicAssetData => {
    const { code, issuer } = ENV_CLASSIC_ASSETS_CONFIG[name][getEnv()];
    const asset = createAsset(code, issuer) as ClassicToken;

    return {
        code,
        issuer,
        assetString: `${code}:${issuer}`,
        asset,
        contract: asset.contractId(getNetworkPassphrase()),
    };
};

export const TESTNET_ASSETS = new Map([
    createTestnetAssetInfo({
        code: USDC_CLASSIC_ASSET_CONFIG[ENV_TESTNET].code,
        image: 'https://static.ultrastellar.com/media/assets/img/ba187c6f-f0e6-45bd-b66b-89ed45640c7d.png',
        home_domain: 'circle.io',
        issuer: USDC_CLASSIC_ASSET_CONFIG[ENV_TESTNET].issuer,
    }),
    createTestnetAssetInfo({
        code: 'USDT',
        image: 'https://static.ultrastellar.com/media/assets/img/de88cd49-1b8e-439d-8dc0-48fb53bde644.png',
        home_domain: 'tether.io',
    }),
    createTestnetAssetInfo({
        code: 'BTC',
        image: 'https://static.ultrastellar.com/media/assets/img/c3380651-52e5-4054-9121-a438c60a1ec4.png',
        home_domain: 'ultrastellar.com',
    }),
    createTestnetAssetInfo({
        code: AQUA_CLASSIC_ASSET_CONFIG[ENV_TESTNET].code,
        image: 'https://static.ultrastellar.com/media/assets/img/1878ee2d-2fd1-4e31-89a7-5a430f1596f8.png',
        home_domain: 'aqua.network',
        issuer: AQUA_CLASSIC_ASSET_CONFIG[ENV_TESTNET].issuer,
    }),
    createTestnetAssetInfo({
        code: GOV_ICE_CODE,
        image: 'https://static.ultrastellar.com/media/assets/img/c2cdac64-386b-4815-9e16-044ae494ceac.png',
        home_domain: 'aqua.network',
        issuer: ICE_ISSUER_TESTNET,
    }),
    createTestnetAssetInfo({
        code: GD_ICE_CODE,
        image: 'https://static.ultrastellar.com/media/assets/img/9694b5ba-3483-451e-85b1-bbadd480da6a.png',
        home_domain: 'aqua.network',
        issuer: ICE_ISSUER_TESTNET,
    }),
    createTestnetAssetInfo({
        code: UP_ICE_CODE,
        image: 'https://static.ultrastellar.com/media/assets/img/c2cdac64-386b-4815-9e16-044ae494ceac.png',
        home_domain: 'aqua.network',
        issuer: ICE_ISSUER_TESTNET,
    }),
    createTestnetAssetInfo({
        code: ICE_CODE,
        image: 'https://static.ultrastellar.com/media/assets/img/c2cdac64-386b-4815-9e16-044ae494ceac.png',
        home_domain: 'aqua.network',
        issuer: ICE_ISSUER_TESTNET,
    }),
    createTestnetAssetInfo({
        code: D_ICE_CODE,
        image: 'https://static.ultrastellar.com/media/assets/img/9694b5ba-3483-451e-85b1-bbadd480da6a.png',
        home_domain: 'aqua.network',
        issuer: ICE_ISSUER_TESTNET,
    }),
    createTestnetAssetInfo({
        code: 'ETH',
        image: 'https://static.ultrastellar.com/media/assets/img/f50535aa-8fcb-487f-912f-96d338b8e610.png',
        home_domain: 'ultrastellar.com',
    }),
    createTestnetAssetInfo({
        code: 'DAI',
        image: 'https://s2.coinmarketcap.com/static/img/coins/200x200/4943.png',
        home_domain: 'makerdao.com',
    }),
]);

export const TESTNET_DISTRIBUTION_AMOUNTS: Array<[string, string]> = [
    createTestnetDistribution({
        code: AQUA_CLASSIC_ASSET_CONFIG[ENV_TESTNET].code,
        amount: '10000000',
        issuer: AQUA_CLASSIC_ASSET_CONFIG[ENV_TESTNET].issuer,
    }),
    createTestnetDistribution({
        code: USDC_CLASSIC_ASSET_CONFIG[ENV_TESTNET].code,
        amount: '10000',
        issuer: USDC_CLASSIC_ASSET_CONFIG[ENV_TESTNET].issuer,
    }),
    createTestnetDistribution({
        code: 'BTC',
        amount: '0.5',
    }),
    createTestnetDistribution({
        code: 'ETH',
        amount: '5',
    }),
    createTestnetDistribution({
        code: 'USDT',
        amount: '10000',
    }),
    createTestnetDistribution({
        code: 'DAI',
        amount: '500',
    }),
];
